import { useState, useContext, memo } from "react";
import { ThemeContext } from "./ThemeContext.jsx";

// ─── PROFILE FRAMES & BADGES ─────���──────────────────────────────────────────
// Animated profile frame borders and achievement badges.
// Frames wrap user avatars with themed animated borders.
// Badges are earned through activity (messages sent, stories created, etc.)
//
// Usage:
//   <ProfileFrame frameId="crimson-pulse" size={48}>
//     <img src={avatarUrl} />
//   </ProfileFrame>
//
//   <AchievementBadge badgeId="storyteller" size="sm" />
//   <BadgeShelf badges={["storyteller", "night-owl", "first-blood"]} />

// ─── FRAME DEFINITIONS ───��──────────────────────────────────────────────────
export const PROFILE_FRAMES = {
  "none": {
    label: "None",
    border: "2px solid var(--ds-line-soft, #2a2a4a)",
    animation: null,
    glow: null,
  },
  "crimson-pulse": {
    label: "Crimson Pulse",
    description: "The blood-red heartbeat of MFML",
    border: "2px solid var(--ds-accent, #c62828)",
    animation: "mfml-frame-pulse-red",
    glow: "0 0 12px var(--ds-accent-a66, rgba(198,40,40,0.4))",
    unlockHint: "Send 100 messages",
  },
  "golden-fate": {
    label: "Golden Fate",
    description: "Touched by destiny itself",
    border: "2px solid var(--ds-gold, #c4a882)",
    animation: "mfml-frame-shimmer-gold",
    glow: "0 0 12px var(--ds-gold-a66, rgba(196,168,130,0.4))",
    unlockHint: "Create 5 stories",
  },
  // Birthday Week 2026 — a gift, not an achievement. Granted by writing the
  // id into profiles.unlocked_frames directly; there is no unlock condition
  // because nobody else can ever earn it.
  "measurers-crown": {
    label: "The Measurer's Crown",
    description: "Violet and gold, the thread woven through the ring — Nicole's belated birthday, August 2026",
    border: "2px solid #9459de",
    animation: "mfml-frame-shimmer-gold",
    glow: "0 0 14px rgba(148,89,222,0.55), 0 0 8px var(--ds-gold-a66, rgba(212,175,55,0.5))",
    unlockHint: "A birthday gift — one of one",
  },
  "purple-arcane": {
    label: "Purple Arcane",
    description: "Channeling ancient magic",
    border: "2px solid var(--ds-accent, #d1b3ff)",
    animation: "mfml-frame-rotate-purple",
    glow: "0 0 14px var(--ds-accent-a56, rgba(209,179,255,0.35))",
    unlockHint: "Chat with 10 different characters",
  },
  "wolf-moon": {
    label: "Wolf Moon",
    description: "Under the pack's protection",
    border: "2px solid #ff9800",
    animation: "mfml-frame-pulse-orange",
    glow: "0 0 10px rgba(255,152,0,0.35)",
    unlockHint: "Be active for 7 consecutive days",
  },
  "siren-tide": {
    label: "Siren's Tide",
    description: "The ocean calls to you",
    border: "2px solid #3498db",
    animation: "mfml-frame-wave-blue",
    glow: "0 0 12px rgba(52,152,219,0.35)",
    unlockHint: "Have a DM conversation over 100 messages",
  },
  "vampire-night": {
    label: "Vampire Night",
    description: "Eternal darkness embraces you",
    border: "2px solid #e74c3c",
    animation: "mfml-frame-flicker-red",
    glow: "0 0 14px rgba(231,76,60,0.4)",
    unlockHint: "Be active between midnight and 4 AM",
  },
  "divine-aura": {
    label: "Divine Aura",
    description: "A god's radiance",
    border: "3px solid transparent",
    animation: "mfml-frame-rainbow",
    glow: "0 0 16px rgba(255,215,0,0.3)",
    unlockHint: "Unlock all other frames",
    gradient: "linear-gradient(#0a0a0f, #0a0a0f) padding-box, linear-gradient(135deg, #ffd700, #d1b3ff, var(--ds-accent, #c62828), #3498db, #ffd700) border-box",
  },
};

// ─── BADGE DEFINITIONS ───────────────────────────────────────��──────────────
export const BADGES = {
  "first-blood": {
    label: "First Blood",
    description: "Sent your first message",
    icon: "🗡️",
    color: "var(--ds-accent, #c62828)",
    rarity: "common",
  },
  "storyteller": {
    label: "Storyteller",
    description: "Created your first story",
    icon: "��",
    color: "var(--ds-gold, #c4a882)",
    rarity: "common",
  },
  "night-owl": {
    label: "Night Owl",
    description: "Active after midnight",
    icon: "🦉",
    color: "#9b59b6",
    rarity: "rare",
  },
  "social-butterfly": {
    label: "Social Butterfly",
    description: "Chatted with 10+ characters",
    icon: "🦋",
    color: "#2ecc71",
    rarity: "rare",
  },
  "heart-breaker": {
    label: "Heartbreaker",
    description: "Reached max relationship with a character",
    icon: "💔",
    color: "#e91e63",
    rarity: "epic",
  },
  "lore-keeper": {
    label: "Lore Keeper",
    description: "Read 50+ community stories",
    icon: "📚",
    color: "#3498db",
    rarity: "epic",
  },
  "fate-weaver": {
    label: "Fate Weaver",
    description: "Created 10+ stories",
    icon: "🕸️",
    color: "#ffd700",
    rarity: "legendary",
  },
  "alpha-presence": {
    label: "Alpha Presence",
    description: "Sent 1,000+ messages",
    icon: "🐺",
    color: "#ff9800",
    rarity: "legendary",
  },
  "founding-soul": {
    label: "Founding Soul",
    description: "Joined during the first week",
    icon: "⭐",
    color: "#ffd700",
    rarity: "legendary",
  },
};

const RARITY_COLORS = {
  common: { bg: "#95a5a633", border: "#95a5a6", glow: "" },
  rare: { bg: "#3498db22", border: "#3498db", glow: "0 0 8px #3498db44" },
  epic: { bg: "var(--ds-accent-a22, #d1b3ff22)", border: "var(--ds-accent, #d1b3ff)", glow: "0 0 10px var(--ds-accent-a44, #d1b3ff44)" },
  legendary: { bg: "var(--ds-gold-a22, #ffd70022)", border: "var(--ds-gold-bright, #ffd700)", glow: "0 0 12px var(--ds-gold-a44, #ffd70044)" },
};

// ─── PROFILE FRAME COMPONENT ──────���─────────────────────────────────────────
export const ProfileFrame = memo(function ProfileFrame({
  frameId = "none",
  size = 48,
  children,
}) {
  const frame = PROFILE_FRAMES[frameId] || PROFILE_FRAMES.none;

  return (
    <div style={{
      width: size, height: size,
      borderRadius: "50%",
      position: "relative",
      flexShrink: 0,
    }}>
      <style>{`
        @keyframes mfml-frame-pulse-red {
          0%, 100% { box-shadow: 0 0 8px var(--ds-accent-a4c, rgba(198,40,40,0.3)); }
          50% { box-shadow: 0 0 16px var(--ds-accent-a99, rgba(198,40,40,0.6)); }
        }
        @keyframes mfml-frame-shimmer-gold {
          0% { box-shadow: 0 0 8px var(--ds-gold-a4c, rgba(196,168,130,0.3)), inset 0 0 4px var(--ds-gold-a1a, rgba(196,168,130,0.1)); }
          50% { box-shadow: 0 0 16px var(--ds-gold-a80, rgba(196,168,130,0.5)), inset 0 0 8px var(--ds-gold-a33, rgba(196,168,130,0.2)); }
          100% { box-shadow: 0 0 8px var(--ds-gold-a4c, rgba(196,168,130,0.3)), inset 0 0 4px var(--ds-gold-a1a, rgba(196,168,130,0.1)); }
        }
        @keyframes mfml-frame-rotate-purple {
          0% { box-shadow: 4px 0 12px rgba(209,179,255,0.4); }
          25% { box-shadow: 0 4px 12px rgba(209,179,255,0.4); }
          50% { box-shadow: -4px 0 12px rgba(209,179,255,0.4); }
          75% { box-shadow: 0 -4px 12px rgba(209,179,255,0.4); }
          100% { box-shadow: 4px 0 12px rgba(209,179,255,0.4); }
        }
        @keyframes mfml-frame-pulse-orange {
          0%, 100% { box-shadow: 0 0 8px rgba(255,152,0,0.3); }
          50% { box-shadow: 0 0 14px rgba(255,152,0,0.5); }
        }
        @keyframes mfml-frame-wave-blue {
          0%, 100% { box-shadow: 0 2px 12px rgba(52,152,219,0.4); }
          50% { box-shadow: 0 -2px 12px rgba(52,152,219,0.4); }
        }
        @keyframes mfml-frame-flicker-red {
          0%, 100% { box-shadow: 0 0 10px rgba(231,76,60,0.4); opacity: 1; }
          50% { box-shadow: 0 0 6px rgba(231,76,60,0.2); opacity: 0.85; }
          75% { box-shadow: 0 0 18px rgba(231,76,60,0.6); opacity: 1; }
        }
        @keyframes mfml-frame-rainbow {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
      `}</style>

      <div style={{
        width: "100%", height: "100%",
        borderRadius: "50%",
        overflow: "hidden",
        border: frame.border,
        background: frame.gradient || "transparent",
        animation: frame.animation ? `${frame.animation} 2.5s ease-in-out infinite` : "none",
        boxShadow: frame.glow || "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        {children}
      </div>
    </div>
  );
});

// ─── ACHIEVEMENT BADGE COMPONENT ───────────────���────────────────────────────
export const AchievementBadge = memo(function AchievementBadge({
  badgeId,
  size = "sm", // "xs" | "sm" | "md" | "lg"
  showLabel = false,
  locked = false,
}) {
  const { theme: _t } = useContext(ThemeContext);
  const badge = BADGES[badgeId];
  if (!badge) return null;

  const rarity = RARITY_COLORS[badge.rarity || "common"] || RARITY_COLORS.common;
  const sizes = { xs: 24, sm: 32, md: 40, lg: 56 };
  const px = sizes[size] || 32;

  return (
    <div style={{
      display: "inline-flex", flexDirection: "column",
      alignItems: "center", gap: "4px",
    }} title={locked ? `🔒 ${badge.description}` : badge.description}>
      <div style={{
        width: px, height: px,
        borderRadius: "var(--ds-r-md, 12px)",
        background: locked ? "var(--ds-surface-3, rgba(114,118,125,0.1))" : rarity.bg,
        border: `1.5px solid ${locked ? "var(--ds-line-soft, #72767d44)" : rarity.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: px * 0.45,
        filter: locked ? "grayscale(1) brightness(0.4)" : "none",
        boxShadow: locked ? "none" : rarity.glow,
        transition: "all 0.2s ease",
        cursor: "default",
        position: "relative",
      }}>
        {badge.icon}
        {locked && (
          <div style={{
            position: "absolute", inset: 0,
            borderRadius: "12px",
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: px * 0.3,
          }}>
            🔒
          </div>
        )}
      </div>
      {showLabel && (
        <span style={{
          fontSize: "9px",
          color: locked ? "var(--ds-ink-low, #72767d)" : badge.color,
          fontWeight: 600,
          letterSpacing: "0.8px",
          textAlign: "center",
          maxWidth: px + 20,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: "'Cinzel', serif",
        }}>
          {badge.label}
        </span>
      )}
    </div>
  );
});

// ─── BADGE SHELF (row of badges) ────────────────��───────────────────────────
export const BadgeShelf = memo(function BadgeShelf({
  earnedBadges = [], // array of badge IDs the user has earned
  showAll = false,   // show locked badges too
  size = "sm",
}) {
  const allBadgeIds = Object.keys(BADGES);
  const displayBadges = showAll
    ? allBadgeIds
    : earnedBadges.filter(id => BADGES[id]);

  if (displayBadges.length === 0) return null;

  return (
    <div style={{
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
      alignItems: "center",
    }}>
      {displayBadges.map(id => (
        <AchievementBadge
          key={id}
          badgeId={id}
          size={size}
          locked={showAll && !earnedBadges.includes(id)}
        />
      ))}
    </div>
  );
});

// ─── FRAME PICKER (for profile settings) ────────────────────────────────────
export function FramePicker({ currentFrame, onSelect, unlockedFrames = [] }) {
  const ctx = useContext(ThemeContext);
  const { theme } = ctx || {};
  const [isMobile] = useState(() => window.innerWidth < 768);

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
      gap: "12px",
    }}>
      {Object.entries(PROFILE_FRAMES).map(([id, frame]) => {
        const isUnlocked = id === "none" || unlockedFrames.includes(id);
        const isActive = currentFrame === id;

        return (
          <div
            key={id}
            onClick={() => isUnlocked && onSelect?.(id)}
            style={{
              padding: "12px",
              borderRadius: "12px",
              border: `1.5px solid ${isActive ? "var(--ds-accent, #c62828)" : theme.borderMedium}`,
              background: isActive ? "var(--ds-accent-a14, rgba(198,40,40,0.08))" : theme.bgSecondary,
              cursor: isUnlocked ? "pointer" : "not-allowed",
              opacity: isUnlocked ? 1 : 0.4,
              textAlign: "center",
              transition: "all 0.2s",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <ProfileFrame frameId={id} size={48}>
              <div style={{
                width: "100%", height: "100%",
                borderRadius: "50%",
                background: "#1a1a2e",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "18px", color: theme.textMuted,
              }}>
                ?
              </div>
            </ProfileFrame>
            <div style={{
              fontSize: "11px", fontWeight: 600,
              color: isActive ? "var(--ds-accent, #c62828)" : `var(--ds-ink, ${(theme?.textSecondary) || "#b5bac1"})`,
              letterSpacing: "0.5px",
            }}>
              {frame.label}
            </div>
            {!isUnlocked && frame.unlockHint && (
              <div style={{ fontSize: "9px", color: `var(--ds-ink-faint, ${(theme?.textFaint) || "#6e5a54"})`, fontStyle: "italic" }}>
                🔒 {frame.unlockHint}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ProfileFrame;
